import type { GA4Item } from '~/gtm/types';
import type { Order, OrderItem, ProductVariantDetails } from '~/types/Order';

/**
 * Convert price from cents to currency units
 */
function formatPrice(priceInCents: number | undefined): number | undefined {
  if (priceInCents === undefined) return undefined;
  return priceInCents / 100;
}

/**
 * Map an OrderItem to GA4 item format
 * TODO: unitPrice is currently WITH VAT - API should provide unitPriceWithoutTax
 */
export function mapOrderItemToGA4Item(orderItem: OrderItem, index?: number): GA4Item {
  const variant = orderItem.variant as ProductVariantDetails | undefined;
  const variantCode = typeof orderItem.variant === 'string' ? orderItem.variant.split('/').pop() : variant?.code;

  const item: GA4Item = {
    item_id: variantCode || 'unknown',
    item_name: orderItem.productName || 'Unknown Product',
    // TODO: Should be price WITHOUT VAT (unitPriceWithoutTax from API)
    price: formatPrice(orderItem.unitPrice),
    quantity: orderItem.quantity || 1,
  };

  // Add variant name if available
  if (variant?.name) {
    item.item_variant = variant.name;
  } else if (variant?.optionValues && variant.optionValues.length > 0) {
    item.item_variant = variant.optionValues.map((ov) => ov.value).join(' / ');
  }

  // Add index if provided
  if (index !== undefined) {
    item.index = index;
  }

  // Add discount if there's an original price
  // TODO: Should be discount WITHOUT VAT
  if (orderItem.originalUnitPrice && orderItem.unitPrice) {
    const discount = orderItem.originalUnitPrice - orderItem.unitPrice;
    if (discount > 0) {
      item.discount = formatPrice(discount);
    }
  }

  return item;
}

/**
 * Map all order items to GA4 items array
 */
export function mapOrderToGA4Items(order: Order): GA4Item[] {
  if (!order.items || order.items.length === 0) {
    return [];
  }

  return order.items.map((item, index) => mapOrderItemToGA4Item(item, index));
}

/**
 * Get the total order value for ecommerce events
 * TODO: Should be order total WITHOUT VAT, without shipping
 * Currently uses itemsSubtotal which includes VAT
 */
export function getOrderValue(order: Order): number {
  // TODO: API should provide itemsSubtotalWithoutTax
  const totalInCents = order.itemsSubtotal ?? 0;
  return totalInCents / 100;
}

/**
 * Get shipping cost from order
 * TODO: Should be shipping cost WITHOUT VAT
 */
export function getShippingValue(order: Order): number {
  // TODO: API should provide shippingTotalWithoutTax
  const shippingInCents = order.shippingTotal ?? 0;
  return shippingInCents / 100;
}

/**
 * Get tax value from order
 */
export function getTaxValue(order: Order): number {
  const taxInCents = order.taxTotal ?? 0;
  return taxInCents / 100;
}
